/**
 * PHOTOFINE PRO - API CLIENT
 * Substitui o Firebase por chamadas REST ao backend Express.
 * Inclui Socket.io para sincronização em tempo real.
 */
import { io } from 'socket.io-client';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Sempre ler token do localStorage para evitar stale state entre abas
const getAuthToken = () => localStorage.getItem('photofine_token') || null;
let socketInstance = null;
const SESSION_MESSAGE = 'Sessão expirada, faça login novamente.';

function createSessionError(code = 'SESSION_MISSING_TOKEN', message = SESSION_MESSAGE, status = 401) {
  const error = new Error(message);
  error.code = code;
  error.status = status;
  return error;
}

function getAuthHeaders({ requireToken = false } = {}) {
  const token = getAuthToken();
  if (requireToken && !token) {
    throw createSessionError('SESSION_MISSING_TOKEN');
  }
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function readJsonSafely(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (error) {
    return {};
  }
}

function throwFotoAiError(response, payload) {
  const status = Number(response?.status || 500);
  const code = String(payload?.code || payload?.errorCode || '').trim();
  if (status === 401 || code === 'SESSION_MISSING_TOKEN' || code === 'SESSION_INVALID_TOKEN') {
    throw createSessionError(code || 'SESSION_INVALID_TOKEN');
  }
  const message = payload?.message || payload?.error || 'Erro ao comunicar com Foto AI';
  const error = new Error(message);
  error.status = status;
  error.code = code || 'FOTO_AI_ERROR';
  throw error;
}

// ============================================
// AUTH
// ============================================
export const setToken = (token) => {
  if (token) {
    localStorage.setItem('photofine_token', token);
  } else {
    localStorage.removeItem('photofine_token');
  }
};

export const getToken = () => getAuthToken();
export const hasSessionToken = () => Boolean(getAuthToken());

export const login = async (email, password) => {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao fazer login');
  setToken(data.token);
  return data;
};

export const getMe = async () => {
  const res = await fetch(`${API_BASE}/api/auth/me`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao verificar usuário');
  return data;
};

export const changePassword = async (currentPassword, newPassword) => {
  const res = await fetch(`${API_BASE}/api/auth/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ currentPassword, newPassword })
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao alterar senha');
  return data;
};

export const logout = () => {
  setToken(null);
  if (socketInstance) {
    socketInstance.disconnect();
    socketInstance = null;
  }
};

// ============================================
// SERVER TIME (Brasil timezone)
// ============================================
let serverTimeCache = null;
let serverTimeFetchPromise = null;

export const fetchServerTime = async () => {
  if (serverTimeCache && Date.now() - serverTimeCache._fetchedAt < 60000) {
    return serverTimeCache;
  }
  if (serverTimeFetchPromise) return serverTimeFetchPromise;
  
  serverTimeFetchPromise = fetch(`${API_BASE}/api/time`)
    .then(res => res.json())
    .then(data => {
      data._fetchedAt = Date.now();
      serverTimeCache = data;
      serverTimeFetchPromise = null;
      return data;
    })
    .catch(() => {
      // Fallback: estimar hora do Brasil a partir do dispositivo
      const now = new Date();
      const brlStr = now.toLocaleString('sv-SE', { timeZone: 'America/Sao_Paulo' });
      const [dateStr, timeStr] = brlStr.split(' ');
      const [year, month, day] = dateStr.split('-').map(Number);
      const fallback = { iso: `${dateStr}T${timeStr}`, date: dateStr, time: timeStr, day, month, year, dayOfWeek: new Date(dateStr).getDay(), timezone: 'America/Sao_Paulo', _fetchedAt: Date.now() };
      serverTimeCache = fallback;
      serverTimeFetchPromise = null;
      return fallback;
    });
  
  return serverTimeFetchPromise;
};

// ============================================
// AI (DEEPSEEK)
// ============================================
export const generateAIDescription = async (taskName, context = '', type = 'description', model = '', images = []) => {
  let res;
  try {
    res = await fetch(`${API_BASE}/api/ai/generate-description`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
      body: JSON.stringify({ taskName, context, type, model, images })
    });
  } catch (networkError) {
    throw new Error('Não foi possível alcançar o servidor de IA. Verifique se o backend está online e acessível.');
  }
  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch (error) {
    const isHtmlLike = /^\s*</.test(text) || /<!doctype html/i.test(text);
    if (isHtmlLike) {
      throw new Error(`A rota de IA retornou HTML (HTTP ${res.status}). Verifique se o deploy está apontando para o backend Node e não só para o frontend.`);
    }
    const preview = String(text || '').replace(/\s+/g, ' ').trim().slice(0, 140);
    if (!preview) {
      throw new Error(`Servidor de IA respondeu vazio (HTTP ${res.status}). Verifique logs do backend e proxy.`);
    }
    throw new Error(`Servidor de IA retornou formato inválido (HTTP ${res.status}). Resposta: ${preview}`);
  }
  if (!res.ok) throw new Error(data.error || 'Erro ao gerar descrição com IA');
  return data;
};


// ============================================
// FOTO AI (assistant embedded via secure backend proxy)
// ============================================
export const fotoAiHealth = async () => {
  const headers = getAuthHeaders({ requireToken: true });
  const res = await fetch(`${API_BASE}/api/foto-ai/health`, {
    headers
  });
  const data = await readJsonSafely(res);
  if (!res.ok) throwFotoAiError(res, data);
  return data;
};

export const fotoAiChat = async ({ message, conversationId = null, quickIntent = null, uiContext = null } = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders({ requireToken: true })
  };
  const res = await fetch(`${API_BASE}/api/foto-ai/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ message, conversationId, quickIntent, uiContext })
  });
  const data = await readJsonSafely(res);
  if (!res.ok) throwFotoAiError(res, data);
  return data;
};

export const fotoAiConfirm = async ({ actionId, approve = true, reason = '', conversationId = null } = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders({ requireToken: true })
  };
  const res = await fetch(`${API_BASE}/api/foto-ai/actions/confirm`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ actionId, approve, reason, conversationId })
  });
  const data = await readJsonSafely(res);
  if (!res.ok) throwFotoAiError(res, data);
  return data;
};

export const fotoAiTts = async ({ text } = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders({ requireToken: true })
  };
  const res = await fetch(`${API_BASE}/api/foto-ai/voice/tts`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ text })
  });
  const data = await readJsonSafely(res);
  if (!res.ok) throwFotoAiError(res, data);
  return data;
};

export const fotoAiStt = async ({ audioBase64, mimeType = 'audio/webm', language = 'pt-BR' } = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders({ requireToken: true })
  };
  const res = await fetch(`${API_BASE}/api/foto-ai/voice/stt`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ audioBase64, mimeType, language })
  });
  const data = await readJsonSafely(res);
  if (!res.ok) throwFotoAiError(res, data);
  return data;
};

export const fotoAiGoogleStatus = async () => {
  const headers = getAuthHeaders({ requireToken: true });
  const res = await fetch(`${API_BASE}/api/foto-ai/google/status`, { headers });
  const data = await readJsonSafely(res);
  if (!res.ok) throwFotoAiError(res, data);
  return data;
};

export const fotoAiGoogleConnectUrl = async () => {
  const headers = getAuthHeaders({ requireToken: true });
  const res = await fetch(`${API_BASE}/api/foto-ai/google/connect-url`, { headers });
  const data = await readJsonSafely(res);
  if (!res.ok) throwFotoAiError(res, data);
  return data;
};

export const fotoAiGoogleDisconnect = async () => {
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders({ requireToken: true })
  };
  const res = await fetch(`${API_BASE}/api/foto-ai/google/disconnect`, { method: 'POST', headers, body: '{}' });
  const data = await readJsonSafely(res);
  if (!res.ok) throwFotoAiError(res, data);
  return data;
};
// ============================================
// DATA (APP STATE)
// ============================================
export const fetchData = async () => {
  const res = await fetch(`${API_BASE}/api/data`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao buscar dados');
  return data.data;
};

export const saveData = async (updates) => {
  const res = await fetch(`${API_BASE}/api/data`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(updates)
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || 'Erro ao salvar dados');
    err.status = res.status;
    throw err;
  }
  return data;
};

// ============================================
// MEMBERS
// ============================================
export const createMember = async (memberData) => {
  const res = await fetch(`${API_BASE}/api/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(memberData)
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao criar membro');
  return data;
};

export const updateMember = async (id, memberData) => {
  const res = await fetch(`${API_BASE}/api/members/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(memberData)
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao atualizar membro');
  return data;
};

export const deleteMember = async (id) => {
  const res = await fetch(`${API_BASE}/api/members/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao excluir membro');
  return data;
};

export const fetchTeamMembers = async () => {
  const res = await fetch(`${API_BASE}/api/members`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao buscar membros');
  return data.members || [];
};

// ============================================
// FILES
// ============================================
export const uploadFile = async (taskId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('taskId', taskId);
  const res = await fetch(`${API_BASE}/api/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getAuthToken()}` },
    body: formData
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao enviar arquivo');
  return data;
};

export const fetchTaskFiles = async (taskId) => {
  const res = await fetch(`${API_BASE}/api/files/${taskId}`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao buscar arquivos');
  return data.files;
};

export const deleteFile = async (fileId) => {
  const res = await fetch(`${API_BASE}/api/files/${fileId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao excluir arquivo');
  return data;
};

export const getFileUrl = (fileId) => {
  return `${API_BASE}/api/files/download/${fileId}?token=${getAuthToken()}`;
};

// ============================================
// BACKUP
// ============================================
export const exportBackup = async () => {
  const res = await fetch(`${API_BASE}/api/backup`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao exportar backup');
  return data;
};

export const restoreBackup = async (backupData) => {
  const res = await fetch(`${API_BASE}/api/backup/restore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ data: backupData })
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao restaurar backup');
  return data;
};

// ============================================
// SOCKET.IO - SINCRONIZAÇÃO EM TEMPO REAL
// ============================================
// ============================================
// MESSAGE HUB (ADMIN)
// ============================================
export const fetchMessageHubMessages = async () => {
  const res = await fetch(`${API_BASE}/api/message-hub/messages`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao buscar mensagens');
  return data.messages || [];
};

export const createMessageHubMessage = async (messageData) => {
  const res = await fetch(`${API_BASE}/api/message-hub/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(messageData)
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao criar mensagem');
  return data.message;
};

export const updateMessageHubMessage = async (id, messageData) => {
  const res = await fetch(`${API_BASE}/api/message-hub/messages/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(messageData)
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao atualizar mensagem');
  return data.message;
};

export const deleteMessageHubMessage = async (id) => {
  const res = await fetch(`${API_BASE}/api/message-hub/messages/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao excluir mensagem');
  return data;
};

export const fetchMessageHubSettings = async () => {
  const res = await fetch(`${API_BASE}/api/message-hub/settings`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao buscar configuracoes de mensagens');
  return data.settings || {
    targetPhone: '',
    evolutionBaseUrl: '',
    evolutionInstance: '',
    evolutionHasApiKey: false,
    evolutionApiKeyMasked: ''
  };
};

export const updateMessageHubTargetPhone = async (value) => {
  const res = await fetch(`${API_BASE}/api/message-hub/settings/target-phone`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ value })
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao atualizar telefone');
  return data.settings || { targetPhone: value };
};

export const updateMessageHubEvolutionSettings = async (payload) => {
  const res = await fetch(`${API_BASE}/api/message-hub/settings/evolution`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(payload || {})
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao atualizar configuração da Evolution API');
  return data.settings || {
    targetPhone: '',
    evolutionBaseUrl: '',
    evolutionInstance: '',
    evolutionHasApiKey: false,
    evolutionApiKeyMasked: ''
  };
};

export const sendWhatsAppMessage = async ({ to, text, meta, leadId } = {}) => {
  const res = await fetch(`${API_BASE}/api/whatsapp/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ to, text, meta, leadId })
  });
  const raw = await res.text();
  let data = {};
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch (error) {
      const isHtmlLike = /^\s*</.test(raw) || /<!doctype html/i.test(raw);
      if (isHtmlLike) {
        throw new Error('A API de envio do WhatsApp não respondeu em JSON. Verifique se o backend atualizado (rota /api/whatsapp/send) e o proxy /api estão ativos.');
      }
      throw new Error('Resposta invalida da API ao enviar WhatsApp.');
    }
  }
  if (!res.ok) throw new Error(data.error || data.message || `Erro ao enviar mensagem pelo WhatsApp (HTTP ${res.status})`);
  return data;
};

// ============================================
// SALES AGENT (MONITOR DE CONVERSAS WHATSAPP)
// ============================================
const parseSalesAgentResponse = async (res, fallbackMessage) => {
  const raw = await res.text();
  let data = {};
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch (error) {
      throw new Error(`${fallbackMessage}. Resposta inválida do servidor.`);
    }
  }
  if (!res.ok) throw new Error(data.error || data.message || fallbackMessage);
  return data;
};

export const fetchSalesAgentLeads = async (status = '', order = 'oldest') => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (order) params.set('order', order);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${API_BASE}/api/sales-agent/leads${qs}`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  const data = await parseSalesAgentResponse(res, 'Erro ao buscar leads');
  return {
    leads: data.leads || [],
    overdue: data.overdue || { d1: 0, d3: 0, d5: 0, d5plus: 0 }
  };
};

export const fetchSalesAgentLeadMessages = async (id) => {
  const res = await fetch(`${API_BASE}/api/sales-agent/leads/${id}/messages`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  return parseSalesAgentResponse(res, 'Erro ao buscar histórico da conversa');
};

export const deleteSalesAgentLead = async (id) => {
  const res = await fetch(`${API_BASE}/api/sales-agent/leads/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  return parseSalesAgentResponse(res, 'Erro ao excluir o contato');
};

export const syncSalesAgentLead = async (id) => {
  const res = await fetch(`${API_BASE}/api/sales-agent/leads/${id}/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({})
  });
  return parseSalesAgentResponse(res, 'Erro ao sincronizar a conversa');
};

export const markSalesAgentLeadResponded = async (id) => {
  const res = await fetch(`${API_BASE}/api/sales-agent/leads/${id}/responded`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({})
  });
  return parseSalesAgentResponse(res, 'Erro ao marcar como respondido');
};

export const markSalesAgentLeadRead = async (id) => {
  const res = await fetch(`${API_BASE}/api/sales-agent/leads/${id}/read`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({})
  });
  return parseSalesAgentResponse(res, 'Erro ao marcar como lida');
};

export const generateSalesAgentSuggestion = async (id) => {
  const res = await fetch(`${API_BASE}/api/sales-agent/leads/${id}/suggest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({})
  });
  return parseSalesAgentResponse(res, 'Erro ao gerar sugestão');
};

export const runSalesAgent = async ({ dryRun = true, force = false, conversationId = null, send = false } = {}) => {
  const res = await fetch(`${API_BASE}/api/sales-agent/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ dryRun, force, conversationId, send })
  });
  return parseSalesAgentResponse(res, 'Erro ao executar o monitor');
};

export const simulateSalesAgentMessage = async ({ phone, text, fromMe = false, pushName = 'Cliente Teste' } = {}) => {
  const res = await fetch(`${API_BASE}/api/sales-agent/simulate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ phone, text, fromMe, pushName })
  });
  return parseSalesAgentResponse(res, 'Erro ao simular mensagem');
};

export const fetchSalesAgentSettings = async () => {
  const res = await fetch(`${API_BASE}/api/sales-agent/settings`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  return parseSalesAgentResponse(res, 'Erro ao buscar configurações do agente');
};

export const updateSalesAgentSettings = async (payload) => {
  const res = await fetch(`${API_BASE}/api/sales-agent/settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(payload || {})
  });
  return parseSalesAgentResponse(res, 'Erro ao salvar configurações do agente');
};

export const testSalesAgentSend = async (text = '') => {
  const res = await fetch(`${API_BASE}/api/sales-agent/test-send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ text })
  });
  return parseSalesAgentResponse(res, 'Erro ao enviar teste');
};

export const fetchSalesAgentWebhookStatus = async () => {
  const res = await fetch(`${API_BASE}/api/sales-agent/webhook-status`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  return parseSalesAgentResponse(res, 'Erro ao consultar o webhook da Evolution');
};

export const fetchSalesAgentDiagnostics = async () => {
  const res = await fetch(`${API_BASE}/api/sales-agent/diagnostics`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  return parseSalesAgentResponse(res, 'Erro ao carregar o diagnóstico do agente');
};

export const registerSalesAgentWebhook = async ({ publicUrl = '', overwrite = false } = {}) => {
  const res = await fetch(`${API_BASE}/api/sales-agent/register-webhook`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ publicUrl, overwrite })
  });
  const raw = await res.text();
  let data = {};
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch (error) {
      throw new Error('Resposta inválida do servidor.');
    }
  }
  if (res.status === 409) return data;
  if (!res.ok) throw new Error(data.error || data.message || 'Erro ao registrar webhook');
  return data;
};

// ============================================
// ALBUM REVIEW PORTAL
// ============================================
const parseApiResponse = async (res, fallbackMessage) => {
  const raw = await res.text();
  let data = {};
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch (error) {
      const isHtmlLike = /^\s*</.test(raw) || /<!doctype html/i.test(raw);
      if (isHtmlLike) {
        throw new Error(`${fallbackMessage}. A rota retornou HTML; verifique se o backend e o proxy /api estão ativos.`);
      }
      throw new Error(`${fallbackMessage}. Resposta inválida do servidor.`);
    }
  }
  if (!res.ok) throw new Error(data.error || data.message || fallbackMessage);
  return data;
};

export const fetchAlbumPendingChanges = async () => {
  const res = await fetch(`${API_BASE}/api/album-reviews/pending-changes`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  return parseApiResponse(res, 'Erro ao buscar alterações pendentes dos álbuns');
};

export const fetchAlbumReview = async (albumId) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/${albumId}`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  return parseApiResponse(res, 'Erro ao buscar revisão do álbum');
};

export const fetchAlbumReviewAcessos = async (reviewId) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/${encodeURIComponent(reviewId)}/acessos`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  return parseApiResponse(res, 'Erro ao buscar acessos do cliente');
};

const isHtmlResponse = async (res) => {
  try {
    const text = await res.clone().text();
    return /^\s*</.test(text) || /<!doctype html/i.test(text);
  } catch { return false; }
};

export const saveAlbumReview = async (payload) => {
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` };
  const body = JSON.stringify(payload || {});
  const tryFetch = async (url, method) => fetch(`${API_BASE}${url}`, { method, headers, body });
  let res;
  // Tentativas em ordem: path alternativo (não interceptado pelo proxy) primeiro,
  // depois os paths originais que o proxy pode bloquear.
  try { res = await tryFetch('/api/save-album-review-project', 'POST'); } catch { res = null; }
  if (!res || await isHtmlResponse(res)) {
    try { res = await tryFetch('/api/save-album-review-project', 'PUT'); } catch { res = null; }
  }
  if (!res || await isHtmlResponse(res)) {
    try { res = await tryFetch('/api/album-reviews', 'POST'); } catch { res = null; }
  }
  if (!res || await isHtmlResponse(res)) {
    try { res = await tryFetch('/api/album-reviews', 'PUT'); } catch { res = null; }
  }
  if (!res || await isHtmlResponse(res)) {
    try { res = await tryFetch('/api/album-reviews-save', 'POST'); } catch { res = null; }
  }
  if (!res || await isHtmlResponse(res)) {
    try { res = await tryFetch('/api/album-reviews-save', 'PUT'); } catch { res = null; }
  }
  if (!res) throw new Error('Erro ao salvar revisão do álbum. Verifique se o backend está ativo.');
  return parseApiResponse(res, 'Erro ao salvar revisão do álbum');
};

export const updateAlbumReview = async (reviewId, payload) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/${reviewId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(payload || {})
  });
  return parseApiResponse(res, 'Erro ao atualizar revisão do álbum');
};

export const uploadAlbumReviewPages = async (reviewId, files = []) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('pages', file));
  const res = await fetch(`${API_BASE}/api/album-reviews/${reviewId}/pages`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getAuthToken()}` },
    body: formData
  });
  return parseApiResponse(res, 'Erro ao enviar lâminas da revisão');
};

export const replaceAlbumReviewPageFile = async (pageId, file) => {
  const formData = new FormData();
  formData.append('page', file);
  const res = await fetch(`${API_BASE}/api/album-reviews/pages/${pageId}/file`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${getAuthToken()}` },
    body: formData
  });
  return parseApiResponse(res, 'Erro ao atualizar arquivo da lâmina');
};

export const reorderAlbumReviewPages = async (reviewId, pageIds = []) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/${reviewId}/pages/reorder`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ pageIds })
  });
  return parseApiResponse(res, 'Erro ao reordenar lâminas');
};

export const deleteAlbumReviewPage = async (pageId) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/pages/${pageId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  return parseApiResponse(res, 'Erro ao remover lâmina');
};

export const deleteAllAlbumReviewPages = async (reviewId) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/${reviewId}/pages`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  return parseApiResponse(res, 'Erro ao remover todas as lâminas');
};

// Limpa paginas e anotacoes de revisoes antigas. O servidor exige
// { confirm: 'EXCLUIR' } no body como protecao contra exclusao acidental.
export const cleanupAlbumReviewRevisions = async (reviewId, strategy) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/${reviewId}/cleanup-revisions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ strategy, confirm: 'EXCLUIR' })
  });
  return parseApiResponse(res, 'Erro ao limpar revisões antigas');
};

export const resetAlbumReviewRevisions = async (reviewId) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/${reviewId}/revisions/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ confirm: 'EXCLUIR' })
  });
  return parseApiResponse(res, 'Erro ao redefinir revisões');
};

export const reopenAlbumReviewApproval = async (reviewId) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/${reviewId}/reopen`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ confirm: 'REABRIR' })
  });
  return parseApiResponse(res, 'Erro ao reabrir aprovação');
};

export const resolveAlbumReviewNote = async (noteId, resolved = true) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/notes/${noteId}/resolve`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ resolved })
  });
  return parseApiResponse(res, 'Erro ao atualizar anotação');
};

export const sendAlbumReviewWhatsApp = async (reviewId, { to, text } = {}) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/${reviewId}/whatsapp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ to, text })
  });
  return parseApiResponse(res, 'Erro ao enviar acesso da revisão pelo WhatsApp');
};

export const markAlbumReviewChangesSent = async (reviewId, { sendWhatsapp = true } = {}) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/${reviewId}/changes-sent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ sendWhatsapp })
  });
  return parseApiResponse(res, 'Erro ao registrar envio de alterações');
};

export const getAlbumReviewAdminPageImageUrl = (pageId, cacheKey = '') => {
  const version = cacheKey ? `&v=${encodeURIComponent(cacheKey)}` : '';
  return `${API_BASE}/api/album-reviews/pages/${pageId}/image?token=${encodeURIComponent(getAuthToken() || '')}${version}`;
};

export const listAlbumReviewRevisions = async (reviewId) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/${reviewId}/revisions`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao listar revisões');
  return res.json();
};

export const startAlbumReviewRevision = async (reviewId, message = '') => {
  // Tenta POST primeiro (mais compativel com proxies restritivos). Se o
  // proxy retornar HTML (caso classico do Traefik do EasyPanel), tenta PUT
  // como fallback. Os dois metodos chamam o mesmo handler no servidor.
  const body = JSON.stringify({ action: 'start-revision', message });
  const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
  const tryFetch = async (method) => {
    const res = await fetch(`${API_BASE}/api/album-reviews/${reviewId}`, {
      method,
      headers,
      body
    });
    return res;
  };
  let res;
  try {
    res = await tryFetch('POST');
  } catch (e) {
    res = await tryFetch('PUT');
  }
  // se o POST veio com HTML, tenta PUT antes de desistir
  try {
    const text = await res.clone().text();
    if (/^\s*</.test(text) || /<!doctype html/i.test(text)) {
      res = await tryFetch('PUT');
    }
  } catch {
    // ignora; parseApiResponse vai tratar
  }
  return parseApiResponse(res, 'Falha ao iniciar nova revisão');
};

export const createAlbumReviewMagicLink = async (reviewId, { expiresInDays = 7, label = '' } = {}) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/${reviewId}/magic-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ expiresInDays, label })
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao gerar link mágico');
  return res.json();
};

export const listAlbumReviewMagicLinks = async (reviewId) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/${reviewId}/magic-links`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao listar links mágicos');
  return res.json();
};

export const revokeAlbumReviewMagicLink = async (reviewId, magicId) => {
  const res = await fetch(`${API_BASE}/api/album-reviews/${reviewId}/magic-link/${magicId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao revogar link');
  return res.json();
};

export const consumeAlbumReviewMagicLink = async (token) => {
  const res = await fetch(`${API_BASE}/api/album-review/magic/${encodeURIComponent(token)}`);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Link mágico inválido');
  return res.json();
};

export const getAlbumReviewApprovalPdfUrl = (reviewToken) => `${API_BASE}/api/album-review/public/approval.pdf?reviewToken=${encodeURIComponent(reviewToken || '')}`;
export const getAlbumReviewAdminApprovalPdfUrl = (reviewId) => `${API_BASE}/api/album-reviews/${reviewId}/approval.pdf?token=${encodeURIComponent(getAuthToken() || '')}`;

export const loginAlbumReview = async (publicToken, username, password) => {
  const res = await fetch(`${API_BASE}/api/album-review/${encodeURIComponent(publicToken)}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password })
  });
  return parseApiResponse(res, 'Erro ao acessar revisão do álbum');
};

export const fetchPublicAlbumReview = async (reviewToken) => {
  const res = await fetch(`${API_BASE}/api/album-review/public`, {
    headers: { Authorization: `Bearer ${reviewToken}` }
  });
  return parseApiResponse(res, 'Erro ao carregar revisão do álbum');
};

export const addPublicAlbumReviewNote = async (reviewToken, { pageId, text, authorName, markerX, markerY } = {}) => {
  const res = await fetch(`${API_BASE}/api/album-review/public/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${reviewToken}` },
    body: JSON.stringify({ pageId, text, authorName, markerX, markerY })
  });
  return parseApiResponse(res, 'Erro ao salvar anotação');
};

export const editPublicAlbumReviewNote = async (reviewToken, noteId, text) => {
  const res = await fetch(`${API_BASE}/api/album-review/public/notes/${encodeURIComponent(noteId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${reviewToken}` },
    body: JSON.stringify({ text })
  });
  return parseApiResponse(res, 'Erro ao atualizar anotação');
};

export const deletePublicAlbumReviewNote = async (reviewToken, noteId) => {
  const res = await fetch(`${API_BASE}/api/album-review/public/notes/${encodeURIComponent(noteId)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${reviewToken}` }
  });
  return parseApiResponse(res, 'Erro ao excluir anotação');
};

export const requestPublicAlbumReviewChanges = async (reviewToken) => {
  const res = await fetch(`${API_BASE}/api/album-review/public/request-changes`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${reviewToken}` }
  });
  return parseApiResponse(res, 'Erro ao solicitar alteração');
};

export const approvePublicAlbumReview = async (reviewToken, { clientName, confirmationText, signatureName, clientCpf, signatureData, termsAccepted, sendCopyToClient } = {}) => {
  const res = await fetch(`${API_BASE}/api/album-review/public/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${reviewToken}` },
    body: JSON.stringify({ clientName, confirmationText, signatureName, clientCpf, signatureData, termsAccepted, sendCopyToClient })
  });
  return parseApiResponse(res, 'Erro ao aprovar projeto');
};

export const getAlbumReviewPublicPageImageUrl = (pageId, reviewToken, cacheKey = '') => {
  const version = cacheKey ? `&v=${encodeURIComponent(cacheKey)}` : '';
  return `${API_BASE}/api/album-review/public/pages/${pageId}/image?reviewToken=${encodeURIComponent(reviewToken || '')}${version}`;
};

// B.1: progresso e timeline de tasks
export const updateTaskProgress = async (taskId, progress, note = '') => {
  const res = await fetch(`${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/progress`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ progress, note })
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao atualizar progresso');
  return res.json();
};

export const addTaskActivity = async (taskId, type, message) => {
  const res = await fetch(`${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/activity`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ type, message })
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao adicionar evento');
  return res.json();
};

// B.3: SLA / tarefas stagnant
export const getStagnantTasks = async (days = 5) => {
  const res = await fetch(`${API_BASE}/api/tasks/stagnant?days=${days}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao listar tarefas stagnant');
  return res.json();
};

export const setTaskSla = async (taskId, slaDays) => {
  const res = await fetch(`${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/sla`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ slaDays })
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao definir SLA');
  return res.json();
};

export const generateStagnantAlert = async (days = 5) => {
  const res = await fetch(`${API_BASE}/api/tasks/alert-stagnant`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ days })
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao gerar alerta');
  return res.json();
};

export const connectSocket = (onDataUpdate, onFileUpdate) => {
  if (socketInstance) {
    socketInstance.disconnect();
  }

  socketInstance = io(API_BASE || window.location.origin, {
    auth: { token: getAuthToken() },
    transports: ['websocket'], // WebSocket only — polling causes excessive bandwidth
    reconnection: true,
    reconnectionAttempts: 3,
    reconnectionDelay: 5000,
    reconnectionDelayMax: 30000
  });

  socketInstance.on('connect', () => {
    console.log('🔌 Socket conectado — id:', socketInstance.id);
  });

  socketInstance.on('connect_error', (err) => {
    console.warn('🔌 Socket connect_error:', err.message);
  });

  socketInstance.on('data-updated', (payload) => {
    if (onDataUpdate) onDataUpdate(payload);
  });

  socketInstance.on('file-uploaded', (payload) => {
    if (onFileUpdate) onFileUpdate(payload);
  });

  socketInstance.on('file-deleted', (payload) => {
    if (onFileUpdate) onFileUpdate(payload);
  });

  socketInstance.on('disconnect', () => {
    // Socket desconectado
  });

  socketInstance.on('connect_error', () => {
    // Erro de conexão Socket.io
  });

  return socketInstance;
};

export const disconnectSocket = () => {
  if (socketInstance) {
    socketInstance.disconnect();
    socketInstance = null;
  }
};

// Assina atualizações do Agente de Vendas (nova mensagem gravada).
export const onSalesAgentUpdated = (callback) => {
  if (!socketInstance || typeof callback !== 'function') return () => {};
  const handler = (payload) => callback(payload);
  socketInstance.on('sales-agent-updated', handler);
  return () => {
    try { socketInstance.off('sales-agent-updated', handler); } catch (error) { /* noop */ }
  };
};

export const onAlbumReviewPendingChanges = (callback) => {
  if (!socketInstance || typeof callback !== 'function') return () => {};
  const handler = (payload) => callback(payload);
  socketInstance.on('album-review-pending-changes', handler);
  return () => {
    try { socketInstance.off('album-review-pending-changes', handler); } catch (error) { /* noop */ }
  };
};

// ============================================
// C.1: CLIENTES (cadastro central)
// ============================================
export const listClients = async (q = '') => {
  const url = q ? `${API_BASE}/api/clients?q=${encodeURIComponent(q)}` : `${API_BASE}/api/clients`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao listar clientes');
  return res.json();
};

export const searchClients = async (q) => {
  if (!q || !q.trim()) return { clients: [] };
  const res = await fetch(`${API_BASE}/api/clients/search?q=${encodeURIComponent(q)}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao buscar clientes');
  return res.json();
};

export const createClient = async ({ name, whatsapp = '', email = '' }) => {
  const res = await fetch(`${API_BASE}/api/clients`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ name, whatsapp, email })
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    const err = new Error(j.error || 'Falha ao criar cliente');
    err.existingId = j.existingId;
    throw err;
  }
  return res.json();
};

export const updateClient = async (id, { name, whatsapp, email, status }) => {
  const res = await fetch(`${API_BASE}/api/clients/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ name, whatsapp, email, status })
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    const err = new Error(j.error || 'Falha ao atualizar cliente');
    err.existingId = j.existingId;
    throw err;
  }
  return res.json();
};

export const deleteClient = async (id) => {
  const res = await fetch(`${API_BASE}/api/clients/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao remover cliente');
  return res.json();
};

// ============================================
// D: SOCIAL MEDIA
// ============================================

// Contas
export const listSocialAccounts = async () => {
  const res = await fetch(`${API_BASE}/api/social/accounts`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao listar contas');
  return res.json();
};

export const createSocialAccount = async ({ platform, account_name, page_id, access_token }) => {
  const res = await fetch(`${API_BASE}/api/social/accounts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ platform, account_name, page_id, access_token })
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao adicionar conta');
  return res.json();
};

export const updateSocialAccount = async (id, data) => {
  const res = await fetch(`${API_BASE}/api/social/accounts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao atualizar conta');
  return res.json();
};

export const deleteSocialAccount = async (id) => {
  const res = await fetch(`${API_BASE}/api/social/accounts/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao remover conta');
  return res.json();
};

// Posts
export const listSocialPosts = async (status = '') => {
  const url = status ? `${API_BASE}/api/social/posts?status=${encodeURIComponent(status)}` : `${API_BASE}/api/social/posts`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao listar posts');
  return res.json();
};

export const getSocialPost = async (id) => {
  const res = await fetch(`${API_BASE}/api/social/posts/${id}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao buscar post');
  return res.json();
};

export const createSocialPost = async ({ content, platforms }) => {
  const res = await fetch(`${API_BASE}/api/social/posts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ content, platforms })
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao criar post');
  return res.json();
};

export const updateSocialPost = async (id, data) => {
  const res = await fetch(`${API_BASE}/api/social/posts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao atualizar post');
  return res.json();
};

export const deleteSocialPost = async (id) => {
  const res = await fetch(`${API_BASE}/api/social/posts/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao remover post');
  return res.json();
};

export const submitSocialPost = async (id) => {
  const res = await fetch(`${API_BASE}/api/social/posts/${id}/submit`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao submeter post');
  return res.json();
};

export const approveSocialPost = async (id, scheduled_at = '') => {
  const res = await fetch(`${API_BASE}/api/social/posts/${id}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ scheduled_at })
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao aprovar post');
  return res.json();
};

export const rejectSocialPost = async (id, reason = '') => {
  const res = await fetch(`${API_BASE}/api/social/posts/${id}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ reason })
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao rejeitar post');
  return res.json();
};

export const publishSocialPost = async (id) => {
  const res = await fetch(`${API_BASE}/api/social/posts/${id}/publish`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao publicar post');
  return res.json();
};

// ============================================
// E: EQUIPAMENTOS E LOCAÇÕES (cabines de foto)
// ============================================
export const listEquipamentos = async () => {
  const res = await fetch(`${API_BASE}/api/equipamentos`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao listar equipamentos');
  return res.json();
};

export const createEquipamento = async (data) => {
  const res = await fetch(`${API_BASE}/api/equipamentos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(data || {})
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao criar equipamento');
  return res.json();
};

export const updateEquipamento = async (id, data) => {
  const res = await fetch(`${API_BASE}/api/equipamentos/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(data || {})
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao atualizar equipamento');
  return res.json();
};

export const deleteEquipamento = async (id) => {
  const res = await fetch(`${API_BASE}/api/equipamentos/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao excluir equipamento');
  return res.json();
};

export const listLocacoes = async (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, v);
  });
  const url = `${API_BASE}/api/locacoes${qs.toString() ? `?${qs.toString()}` : ''}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao listar locações');
  return res.json();
};

const handleLocacaoResponse = async (res, fallbackMessage) => {
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(payload.error || fallbackMessage);
    err.status = res.status;
    if (Array.isArray(payload.conflicts)) err.conflicts = payload.conflicts;
    throw err;
  }
  return payload;
};

export const createLocacao = async (data) => {
  const res = await fetch(`${API_BASE}/api/locacoes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(data || {})
  });
  return handleLocacaoResponse(res, 'Falha ao criar locação');
};

export const updateLocacao = async (id, data) => {
  const res = await fetch(`${API_BASE}/api/locacoes/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(data || {})
  });
  return handleLocacaoResponse(res, 'Falha ao atualizar locação');
};

export const deleteLocacao = async (id) => {
  const res = await fetch(`${API_BASE}/api/locacoes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao excluir locação');
  return res.json();
};

export const cancelarLocacao = async (id) => {
  const res = await fetch(`${API_BASE}/api/locacoes/${encodeURIComponent(id)}/cancelar`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getAuthToken()}` },
    body: '{}'
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao cancelar locação');
  return res.json();
};

export const reativarLocacao = async (id) => {
  const res = await fetch(`${API_BASE}/api/locacoes/${encodeURIComponent(id)}/reativar`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getAuthToken()}` },
    body: '{}'
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao reativar locação');
  return res.json();
};

// Disponibilidade pública (consultada pelo site externo vocenaphoto)
export const fetchDisponibilidade = async (data) => {
  const res = await fetch(`${API_BASE}/api/equipamentos/disponibilidade?data=${encodeURIComponent(data)}`);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao consultar disponibilidade');
  return res.json();
};

// ============================================
// F: FREELANCES
// ============================================
export const listFreelancers = async () => {
  const res = await fetch(`${API_BASE}/api/freelancers`, {
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao listar freelances');
  return res.json();
};

export const createFreelancer = async (data) => {
  const res = await fetch(`${API_BASE}/api/freelancers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(data || {})
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao criar freelance');
  return res.json();
};

export const updateFreelancer = async (id, data) => {
  const res = await fetch(`${API_BASE}/api/freelancers/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
    body: JSON.stringify(data || {})
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao atualizar freelance');
  return res.json();
};

export const deleteFreelancer = async (id) => {
  const res = await fetch(`${API_BASE}/api/freelancers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Falha ao excluir freelance');
  return res.json();
};

// Reenvia o convite de escala (WhatsApp) para um freelance vinculado
export const enviarConviteFreelance = async (locacaoId, freelancerId) => {
  const res = await fetch(`${API_BASE}/api/locacoes/${encodeURIComponent(locacaoId)}/freelancers/${encodeURIComponent(freelancerId)}/convidar`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getAuthToken()}` },
    body: '{}'
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(payload.error || 'Falha ao enviar convite');
    err.link = payload.link;
    err.whatsappStatus = payload.whatsappStatus;
    throw err;
  }
  return payload;
};

// ============================================
// G: ESCALA DE FREELANCE (página pública)
// ============================================
export const fetchEscala = async (token) => {
  const res = await fetch(`${API_BASE}/api/escala/${encodeURIComponent(token)}`);
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(payload.error || 'Não foi possível carregar a escala');
    err.status = res.status;
    throw err;
  }
  return payload;
};

export const responderEscala = async (token, resposta) => {
  const res = await fetch(`${API_BASE}/api/escala/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resposta })
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'Não foi possível registrar a resposta');
  return payload;
};
